import { Column, ColumnDef } from '@tanstack/react-table';
import { Opening } from 'api-clients/monolith';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import PipelineApplicants from '@/assets/icons/pipeline-applicants.svg';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { HiringGoal } from '@/components/ui/hiring-goal';
import { OpeningStatus } from '@/components/ui/opening-status';
import { SortColumnButton } from '@/components/ui/sort-column-button';
import { Typography } from '@/components/ui/typography';
import { useHireUrl } from '@/hooks/use-hire-url';

export function useOpeningColumns(): ColumnDef<Opening>[] {
  const { getHireApplicantTableUrl } = useHireUrl();

  const handleSort = (column: Column<Opening>) => {
    column.toggleSorting(column.getIsSorted() === 'asc');
  };

  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ),
    },
    {
      accessorKey: 'funnel_name',
      header: ({ column }) => (
        <SortColumnButton onSort={() => handleSort(column)}>
          <FormattedMessage
            defaultMessage="Opening name"
            description="Openings list: job title (links to applicant table in Hire)"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <div className="flex min-w-[240px] items-center gap-1">
          <OpeningStatus active={row.original.active} />
          <Button variant="link" className="h-auto text-left whitespace-normal">
            <Link
              to={getHireApplicantTableUrl(
                row.original.brand_slug ?? 'no_brand',
                row.original.location_id,
                row.original.funnel_external_id,
              )}
              target="_blank"
            >
              {row.original.funnel_name}
            </Link>
          </Button>
        </div>
      ),
    },
    {
      accessorKey: 'brand_name',
      header: ({ column }) => (
        <SortColumnButton onSort={() => handleSort(column)}>
          <FormattedMessage
            defaultMessage="Brand"
            description="Openings table column: employer brand"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.brand_name}</Typography>
      ),
    },
    {
      accessorKey: 'location_name',
      header: ({ column }) => (
        <SortColumnButton onSort={() => handleSort(column)}>
          <FormattedMessage
            defaultMessage="Location & Address"
            description="Openings table: work site name plus street line"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <div className="flex min-w-[320px] flex-col items-start overflow-hidden">
          <Typography variant="subtitle2" className="whitespace-break-spaces">
            {row.original.location_name}
          </Typography>
          <Typography variant="subtitle1" className="whitespace-break-spaces">
            {row.original.location_address}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'position_name',
      header: ({ column }) => (
        <SortColumnButton onSort={() => handleSort(column)}>
          <FormattedMessage
            defaultMessage="Position"
            description="Openings table: job title / requisition"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <div className="max-w-[240px] overflow-hidden">
          <Typography variant="subtitle1" className="whitespace-break-spaces">
            {row.original.position_name}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'owner_name',
      header: ({ column }) => (
        <SortColumnButton onSort={() => handleSort(column)}>
          <FormattedMessage
            defaultMessage="Opening owner"
            description="Table column: recruiter or hiring manager on the requisition"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.owner_name}</Typography>
      ),
    },
    {
      accessorKey: 'hiring_goal',
      header: () => (
        <FormattedMessage
          defaultMessage="Hiring goal"
          description="Column: current hire-by target for the opening (if any)"
        />
      ),
      cell: ({ row }) => (
        <HiringGoal
          hiringGoal={row.original.active_hiring_goal}
          brand={row.original.brand_slug ?? 'no_brand'}
          location={row.original.location_id}
          funnel={row.original.funnel_external_id}
        />
      ),
    },
    {
      accessorKey: 'pipeline_count',
      header: () => (
        <FormattedMessage
          defaultMessage="Pipeline"
          description="Column: applicants currently in the hiring pipeline"
        />
      ),
      cell: ({ row }) => (
        <div className="flex gap-2">
          <img src={PipelineApplicants} alt="Target" />
          <Typography variant="subtitle1">
            {row.original.pipeline_count}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'is_selected_for_sourcing',
      header: () => (
        <FormattedMessage
          defaultMessage="In dash"
          description="Column: whether the opening is on the org sourcing dashboard"
        />
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">
          {row.original.is_selected_for_sourcing ? 'Yes' : 'No'}
        </Typography>
      ),
    },
  ];
}
